import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './GamePopup.css';

/**
 * 루미나 화면 층 신작 — 블랙 `components/common/GamePopup.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 gp- 차콜 머리 줄 규격).
 * WHY: SPEC §2 — 열릴 때 전체화면 자동 진입, 전체화면 상태 감시, ESC 닫기, 탭 닫기 신호(sendBeacon), 닫을 때 전체화면 해제는 원본 그대로.
 *      머리 줄(게임명·전체화면·닫기)만 루미나 규격(차콜 바 + 36px 원형 아이콘 버튼, 인라인 SVG)으로 그린다.
 *      `.game-popup-overlay` 클래스는 원본 로직이 querySelector 로 잡으므로 이름을 유지한다.
 */
const GamePopup = ({ game, gameUrl, onClose, loading = false, customTitle }) => {
  const [isFullscreen, setIsFullscreen] = useState(true); // 기본값 전체화면

  // 팝업 열릴 때 자동으로 전체화면 모드 진입
  useEffect(() => {
    const enterFullscreen = async () => {
      const popupElement = document.querySelector('.game-popup-overlay');
      if (popupElement && !document.fullscreenElement) {
        try {
          if (popupElement.requestFullscreen) {
            await popupElement.requestFullscreen();
          } else if (popupElement.mozRequestFullScreen) {
            await popupElement.mozRequestFullScreen();
          } else if (popupElement.webkitRequestFullscreen) {
            await popupElement.webkitRequestFullscreen();
          } else if (popupElement.msRequestFullscreen) {
            await popupElement.msRequestFullscreen();
          }
          setIsFullscreen(true);
        } catch (err) {
          console.log('전체화면 진입 실패:', err);
          // 전체화면 진입 실패해도 CSS로 전체화면처럼 표시
          setIsFullscreen(true);
        }
      }
    };

    // 약간의 딜레이 후 전체화면 진입 (DOM 렌더링 완료 대기)
    const timer = setTimeout(enterFullscreen, 100);
    return () => clearTimeout(timer);
  }, []);

  // 전체화면 상태 변경 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    // ESC 키로 팝업 닫기
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // 브라우저 창/탭 닫기 시 게임 종료 처리 (sendBeacon)
  // GamePopup이 열려있는 동안 브라우저를 닫으면 게임 세션을 정상 종료
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('token');
      if (token) {
        navigator.sendBeacon('/api/games/end-session-beacon', JSON.stringify({ token }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const toggleFullscreen = () => {
    const popupElement = document.querySelector('.game-popup-overlay');

    if (!document.fullscreenElement) {
      if (popupElement.requestFullscreen) {
        popupElement.requestFullscreen();
      } else if (popupElement.mozRequestFullScreen) {
        popupElement.mozRequestFullScreen();
      } else if (popupElement.webkitRequestFullscreen) {
        popupElement.webkitRequestFullscreen();
      } else if (popupElement.msRequestFullscreen) {
        popupElement.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const handleClose = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
    setIsFullscreen(false);
    onClose();
  };

  const title = customTitle || game?.game_name_ko || game?.game_name;

  /* [2026-10-31] 게임 창은 페이지 맨 바깥(body)에 그린다 — 공용 알림창(Modal.jsx)과 같은 방식.
     WHY: 이 창을 여는 화면(슬롯·홈·미니게임)은 본문(main) 안에 있고, 본문은 겹침 층 1로 묶여 있다(styles/lumina.css main).
          그 안에서는 이 창의 겹침 10000·99999 가 바깥과 비교되지 않아, 상단 메뉴(60)·하단 내비(66)·상담 버튼(72)이 게임 위에 그려졌다.
          브라우저 전체화면이 안 되는 경우(요소 전체화면 기능이 없는 아이폰 사파리, 전체화면을 빠져나온 데스크톱·안드로이드)
          게임 화면 위 약 140px·아래 약 60px 와 닫기(×)가 가려졌다(2026-10-31 ur3 실측). 전체화면 진입·ESC·종료 통보 동작은 그대로다. */
  return createPortal(
    <div className={`game-popup-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className={`game-popup ${game?.game_type === 'slot' ? 'slot-size' : 'casino-size'}`}>
        <div className="game-popup-header">
          <h3>{title}</h3>
          <div className="game-popup-controls">
            <button className="game-popup-fullscreen" type="button" onClick={toggleFullscreen} title={isFullscreen ? '전체화면 종료' : '전체화면'} aria-label={isFullscreen ? '전체화면 종료' : '전체화면'}>
              {isFullscreen
                ? <svg viewBox="0 0 24 24"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>
                : <svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>}
            </button>
            <button className="game-popup-close" type="button" onClick={handleClose} title="닫기" aria-label="닫기">
              <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </div>
        </div>
        <div className="game-popup-content">
          {loading ? (
            <div className="game-loading"><span className="gp-spin"></span><span>게임을 불러오는 중...</span></div>
          ) : (
            <iframe
              src={gameUrl}
              className="game-iframe"
              title={title || "Game"}
              frameBorder="0"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GamePopup;
